import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const FORM_ID = "db74808d6mcdj80oo7i0";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db74808d6mcdj80oo7i0";

const contactItems = [
  {
    icon: "ri-mail-send-line",
    label: "联系邮箱",
    value: "contact@autonews-demo.com",
    hint: "内容建议、纠错与合作均可发送邮件",
  },
  {
    icon: "ri-phone-line",
    label: "联系电话",
    value: "400-000-0000",
    hint: "工作日 9:00 - 18:00（演示号码）",
  },
  {
    icon: "ri-map-pin-line",
    label: "办公地址",
    value: "北京市朝阳区汽车产业园 1 号楼",
    hint: "来访请提前预约（演示地址）",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useDocumentMeta({
    title: "联系我们 - 汽车资讯网",
    description:
      "通过在线留言表单或邮箱、电话与汽车资讯网取得联系，我们欢迎内容建议、信息纠错与商务合作咨询。",
    keywords: "联系汽车资讯网,在线留言,内容建议,商务合作",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // honeypot check (before any async work)
    const honey = String(formData.get("website_alt") ?? "").trim();
    if (honey) {
      setStatus("success");
      setSuccessMsg("感谢你的留言，我们已收到（演示反馈）。");
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") params.append(key, value);
    });

    setStatus("submitting");
    setFormError("");
    setSuccessMsg("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam = /spam|form data is spam/i.test(String(serverMsg));

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setSuccessMsg("提交成功，感谢你的反馈，我们会尽快处理。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请检查内容后重试。");
      }
    } catch (err) {
      setStatus("error");
      setFormError(
        err instanceof Error ? err.message : "网络异常，提交失败，请稍后重试。",
      );
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "联系我们" }]} />

      <div className="mt-4 bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
          联系我们
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl">
          如果你对内容有建议、发现信息需要纠正，或希望与我们开展合作，欢迎通过下方方式联系我们。请填写留言表单，我们会认真阅读每一条信息。
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="flex flex-col gap-4">
          {contactItems.map((item) => (
            <div
              key={item.label}
              className="bg-background-50 border border-background-200 rounded-lg p-5 flex gap-4"
            >
              <span className="w-12 h-12 shrink-0 rounded-lg bg-primary-600 flex items-center justify-center">
                <i className={`${item.icon} text-xl text-background-50`} />
              </span>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500">{item.label}</p>
                <p className="text-sm md:text-base text-foreground-950 font-medium mt-0.5 break-all">
                  {item.value}
                </p>
                <p className="text-xs text-foreground-500 mt-1">{item.hint}</p>
              </div>
            </div>
          ))}

          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden h-56">
            <iframe
              title="办公位置地图（演示）"
              src="https://emc88.cn/maps?q=Beijing%20Chaoyang&output=embed"
              className="w-full h-full border-0"
              aria-label="汽车资讯网办公位置地图"
            />
          </div>
        </div>

        <div className="lg:col-span-2 bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
          <h2 className="font-heading font-semibold text-xl text-foreground-950 mb-1">
            在线留言
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            带 <span className="text-accent-600">*</span> 的为必填项，留言内容不超过 500 字。
          </p>

          <form
            id={FORM_ID}
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className="text-sm text-foreground-700">
                  姓名 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm text-foreground-700">
                  邮箱 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收我们的回复"
                  className="h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="topic" className="text-sm text-foreground-700">
                留言类型
              </label>
              <select
                id="topic"
                name="topic"
                defaultValue="内容建议"
                className="h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="内容建议">内容建议</option>
                <option value="信息纠错">信息纠错</option>
                <option value="商务合作">商务合作</option>
                <option value="其他反馈">其他反馈</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="message" className="text-sm text-foreground-700">
                留言内容 <span className="text-accent-600">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请描述你的建议或问题，最多 500 字（演示表单）"
                className="px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 resize-y"
              />
            </div>

            {/* anti-spam field */}
            <input
              name="website_alt"
              type="text"
              className="contact-verify-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="h-11 px-6 rounded-md bg-primary-600 text-background-50 text-sm flex items-center justify-center gap-1.5 hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-fit"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    提交留言
                  </>
                )}
              </button>
              <p className="text-xs text-foreground-400">
                提交的留言会通过平台表单能力收集，可在表单数据中查看。
              </p>
            </div>

            {status === "success" && (
              <div className="flex items-start gap-2 text-sm text-primary-700 bg-primary-100 rounded-md px-4 py-3">
                <i className="ri-checkbox-circle-line mt-0.5" />
                <span>{successMsg}</span>
              </div>
            )}
            {status === "error" && (
              <div className="flex items-start gap-2 text-sm text-accent-800 bg-accent-100 rounded-md px-4 py-3">
                <i className="ri-error-warning-line mt-0.5" />
                <span>{formError}</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}